import {
  ChevronDownIcon,
  ChevronUpIcon,
  Cross2Icon,
  MagnifyingGlassIcon,
} from '@radix-ui/react-icons';
import {
  Box,
  Flex,
  Popover,
  ScrollArea,
  Text,
  TextField,
} from '@radix-ui/themes';
import { phoneLikeDigits } from 'classes/helpers/phone.helper';
import { CommonChecklist } from 'components/common/checklist';
import { CommonInputHint } from 'components/common/form/hint';
import { CommonInputLabel } from 'components/common/form/label';
import { HINT_DELIMITER } from 'components/common/form/text-hint';
import { CommonInputWrapper } from 'components/common/form/wrapper';
import useOnClickAway from 'hooks/useOnClickAway';
import { t } from 'i18next';
import { ISearchInput } from 'interfaces/forms/search';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { useCallback, useMemo, useRef, useState } from 'react';

const AUTO_CLOSE_POPOVER = true;
const AUTO_CLEAR_SEARCH = false;
const MAX_ITEMS = 50;

// used for generating all options and narrowed search options while preserving any group entries/labels
const getSearchOptions = (config: {
  options: ISearchOption[];
  skipSort?: boolean;
  reverseSort?: boolean;
}): ISearchOption[] => {
  // ignores invisible and any group options
  const relevantOptions = config.options
    .filter((o) => !o.invisible)
    .filter((o) => !o.isGroup);

  const groupDict = ArrayHelper.groupBy(relevantOptions, 'group');

  const groupKeys = Object.keys(groupDict);

  // sort the groups
  if (!config.skipSort) {
    groupKeys.sort();

    if (config.reverseSort) {
      groupKeys.reverse();
    }
  }

  const output: ISearchOption[] = [];

  groupKeys.forEach((key) => {
    const groupOptions = groupDict[key];

    if (!groupOptions) {
      return;
    }

    if (groupOptions.length === 0) {
      return;
    }

    if (key) {
      output.push({
        label: key,
        value: key,
        group: key,
        isGroup: true,
        disabled: true,
        hideControl: true,
        searchKeys: [key, ...MiscHelper.keyify(key)],
      });
    }

    // sort the options w/n each group
    if (!config.skipSort) {
      groupOptions.sort((a, b) => StringHelper.safeSort(a.label, b.label));

      if (config.reverseSort) {
        groupOptions.reverse();
      }
    }

    output.push(
      ...groupOptions.map((m) => {
        const o: ISearchOption = {
          ...m,

          searchKeys:
            m.searchKeys && m.searchKeys.length > 0
              ? m.searchKeys // skip regenerating searchKeys if already populated
              : [
                  // original value for strict matching to find the original
                  m.value,
                  // keys from various attributes for fuzzy matching (e.g. by group name)
                  ...MiscHelper.keyify(
                    [m.value, m.label, m.group ?? ''].join(' ')
                  ),
                  // similar to above, but maintains multi-word aliases as-is for phrase matching
                  ...(m.aliases?.map((a) => MiscHelper.sanitizeKey(a)) ?? []),
                ],
        };

        return o;
      })
    );
  });

  return output;
};

type ISearchOption = IOption & {
  searchKeys?: string[];
};

export const CommonSearchInput = (props: ISearchInput) => {
  const [checklistKey, setChecklistKey] = useState(Date.now());
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);

  /*
    Close the wrapping popover clicking outside of it:
    
    Radix Popover handles this fine on desktop, but there are certain cases
    where the popover won't close properly on mobile devices (i.e. when you click a table element)
  */

  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const onClickAway = useCallback(() => {
    setOpen(false);
  }, []);

  useOnClickAway({
    ref: popoverRef,
    shouldListen: open,
    onClickAway,
    // don't handle clicks on the trigger element to prevent re-opening popover
    ignoreEls: [triggerRef.current],
  });

  const search = (query: string) => {
    setSearchTerm(query);

    if (props.onSearch) {
      props.onSearch(query);
    }
  };

  const groupedOptions = useMemo(
    () =>
      getSearchOptions({
        options: props.options,
        skipSort: props.skipSort,
        reverseSort: props.reverseSort,
      }),
    [props.options, props.skipSort, props.reverseSort]
  );

  const slotIcon = useMemo(() => {
    if (props.rightSlot === null) {
      return undefined;
    }

    if (props.rightSlot) {
      return props.rightSlot;
    }

    if (props.disabled) {
      // don't show any icon since the user can't use it anyway
      return undefined;
    }

    if (props.optional && props.values && props.values.length > 0) {
      return (
        <Cross2Icon
          onClick={() => {
            if (props.disabled) {
              return;
            }

            props.onChange([]);
          }}
        />
      );
    }

    if (open) {
      return <ChevronUpIcon />;
    }

    return (
      <ChevronDownIcon
        onClick={() => {
          if (props.disabled) {
            return;
          }

          setOpen(true);
        }}
      />
    );
  }, [
    props.rightSlot,
    props.disabled,
    props.optional,
    props.values,
    props.onChange,
    open,
  ]);

  const displayValue = useMemo(() => {
    if (props.displayValue) {
      return props.displayValue;
    }

    if (!props.values) {
      return '';
    }

    if (props.values.length === 0) {
      return '';
    }

    const maxDisplayItems = props.maxDisplayItems ?? 1;
    const uniqueValues = ArrayHelper.unique(props.values);

    if (uniqueValues.length <= maxDisplayItems) {
      const selected = props.options
        .filter((o) => uniqueValues.includes(o.value))
        .slice(0, maxDisplayItems); // e.g. if multiple options have the same value

      const displayItems = selected
        .map((s) => {
          // combine label and suffix label with a ·
          const parts = [s?.label ?? '', s?.suffixLabel ?? ''].filter((s) => s);
          return parts.join(HINT_DELIMITER);
        })
        // filter items that have neither a label nor a suffix label
        .filter((s) => s);

      if (displayItems.length > 0) {
        return displayItems.map((s) => t(s).toString()).join(', ');
      }

      return uniqueValues.join(', ');
    }

    // todo: add translation key for this
    return `${props.values.length} options selected`;
  }, [props.displayValue, props.values, props.options, props.maxDisplayItems]);

  const placeholder = t(
    props.placeholder ??
      (props.hideSearch
        ? 'common.select-placeholder'
        : 'common.search-placeholder')
  ).toString();

  const matchingOptions = useMemo(() => {
    const safeSearchTerms = props.strict
      ? [searchTerm.toLowerCase()]
      : MiscHelper.keyify(searchTerm);

    const digitTerm = props.matchDigits
      ? phoneLikeDigits(searchTerm)
      : undefined;

    if (digitTerm && !safeSearchTerms.includes(digitTerm)) {
      safeSearchTerms.push(digitTerm);
    }

    if (safeSearchTerms.length === 0) {
      return groupedOptions;
    }

    return getSearchOptions({
      options: groupedOptions.filter((o) =>
        ArrayHelper.hasSubstringIntersection(
          o.searchKeys ?? [],
          safeSearchTerms
        )
      ),
      skipSort: props.skipSort,
      reverseSort: props.reverseSort,
    });
  }, [
    props.strict,
    props.matchDigits,
    props.skipSort,
    props.reverseSort,
    searchTerm,
    groupedOptions,
  ]);

  const warnRequired = props.required && props.values.length === 0;

  const trigger = useMemo(() => {
    if (props.renderTrigger) {
      return props.renderTrigger(props.values, slotIcon);
    }

    return (
      <TextField.Root
        size={props.size}
        className={`cursor-pointer ${props.className ?? ''}`}
        disabled={props.disabled}
        color={props.inputColor}
        name={props.name}
        placeholder={placeholder}
        required={!props.optional}
        value={displayValue}
        onChange={() => {
          // onChange provided to suppress error re: value w/o onChange
        }}
        // prevent the cursor from entering this input (on click or clear) so users don't think they can type into it
        onFocus={(event) => event.currentTarget.blur()}
        type="text"
        autoCapitalize="off"
        autoCorrect="off"
        autoComplete="off"
      >
        <TextField.Slot side="right">{slotIcon}</TextField.Slot>
      </TextField.Root>
    );
  }, [
    props.renderTrigger,
    props.values,
    slotIcon,
    props.size,
    props.className,
    props.disabled,
    props.inputColor,
    props.name,
    placeholder,
    props.optional,
    displayValue,
  ]);

  // * Radix ScrollArea will trigger a strange resize observer error if multiple + hideSearch are both true unless you set asChild
  // * see: https://github.com/WICG/resize-observer/issues/38
  const asChild = props.multiple && props.hideSearch;

  const controls = (
    <Flex direction="column" gap={RADIX.FLEX.GAP.INPUT}>
      {!props.hideSearch && (
        <Box minWidth={props.contentMinWidth} maxWidth={props.contentMaxWidth}>
          <TextField.Root
            className={props.className}
            disabled={props.disabled}
            name={props.name}
            type="text"
            value={searchTerm}
            placeholder={
              props.options.length > MAX_ITEMS
                ? 'Type to find more options'
                : undefined
            }
            onChange={(e) => {
              const v = e.target.value;
              search(v);
            }}
            autoCapitalize="off"
            autoCorrect="off"
            autoComplete="off"
          >
            <TextField.Slot side="right">
              {searchTerm ? (
                <Cross2Icon
                  className="cursor-pointer"
                  onClick={() => search('')}
                />
              ) : (
                <MagnifyingGlassIcon />
              )}
            </TextField.Slot>
          </TextField.Root>
        </Box>
      )}

      <ScrollArea
        type="auto"
        scrollbars="vertical"
        style={{ maxHeight: 250 }}
        asChild={asChild}
      >
        {matchingOptions.length > 0 ? (
          <Box
            minWidth={props.contentMinWidth}
            maxWidth={props.contentMaxWidth}
          >
            <CommonChecklist
              key={checklistKey}
              id={`${props.id}-search-checklist`}
              hierarchy={props.hierarchy}
              btnSize={props.btnSize}
              options={matchingOptions}
              onChange={(v) => {
                const shouldProceed = props.shouldChange
                  ? props.shouldChange(v)
                  : true;

                if (!shouldProceed) {
                  // checklist will re-render as if nothing happened
                  setChecklistKey(Date.now());
                  return;
                }

                props.onChange(v);

                if (AUTO_CLOSE_POPOVER && !props.multiple) {
                  setOpen(false);
                }
              }}
              values={props.values}
              as={props.multiple ? 'checkbox' : 'button'}
              truncateTo={MAX_ITEMS}
              singleSearch={!props.multiple}
            />
          </Box>
        ) : (
          <Box
            p="1"
            minWidth={props.contentMinWidth}
            maxWidth={props.contentMaxWidth}
          >
            <Text color={RADIX.COLOR.SECONDARY}>
              {t('common.no-results-found')}
            </Text>
          </Box>
        )}
      </ScrollArea>
    </Flex>
  );

  const content = (
    <Popover.Root
      open={open}
      onOpenChange={(value) => {
        setOpen(value);

        if (AUTO_CLEAR_SEARCH && !value) {
          // clear search on popover close
          search('');
        }
      }}
    >
      <Popover.Trigger disabled={props.disabled} ref={triggerRef}>
        {trigger}
      </Popover.Trigger>

      <Popover.Content className="BorderedContent" ref={popoverRef}>
        {/* for performance, don't draw the stuff unless the search is open */}
        {open && controls}
      </Popover.Content>
    </Popover.Root>
  );

  return (
    <CommonInputWrapper {...props}>
      <CommonInputLabel {...props} />

      {content}

      <CommonInputHint {...props} />

      {warnRequired && (
        <Text
          color={RADIX.COLOR.DANGER}
          size={RADIX.TEXT.SIZE.HINT}
          className="CommonInputHint"
        >
          This field is required
        </Text>
      )}
    </CommonInputWrapper>
  );
};
